<template>
  <div
    v-if="service"
    class="main-operations"
  >
    <div class="queues-list">
      <!-- If system status message exists -->

      <!--
        When service is off:
        - add "disabled" attribute to each "queue-row" and to "main-sidebar"
        - add "title" attribute to each "queue-row" and to "main-sidebar" with the same content as the status message paragraph (ex.: 'Service is switched off')
        - add class "btn-disabled" to btn-call
      -->

      <div
        v-if="!service.active"
        class="service-status-message"
      >
        <p>
          {{ $t('service.is.off') }}
          <router-link
            :to="{name: 'GeneralSettingsPage', params: { serviceID }}"
          >
            {{ $t('change.settings') }}
          </router-link>
        </p>
      </div>
      <!-- Próxima -->
      <div
        :disabled="!service.active || !loggedUser.desk || nextTicket === null"
        class="queue-row queue-active queue-call-next flex"
      >
        <div class="queue">
          <div class="queue-name">
            {{ $t('next') }}
          </div>
        </div>
        <div class="tickets-waiting">
          <!-- dont put anything here -->
        </div>

        <div class="next-ticket">
          <div
            id="global-next-ticket-letter"
            class="next-ticket-letter"
          >
            {{ nextTicket | ifTicketNull('letter', '--') }}
          </div>
          <div
            id="global-next-ticket-number"
            class="next-ticket-number"
          >
            {{ nextTicket | ifTicketNull('number', '') }}
          </div>
        </div>

        <wait-time
          :next-ticket="nextTicket"
        />

        <div class="queue-action">
          <button
            id="general-call-btn"
            :disabled="callBtnDisable || nextTicket === null"
            class="btn-call"
            title=""
            @click="callNextTicket({queue: null})"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g
                class="icon-call"
                fill-rule="nonzero"
                fill="#0076DC"
              >
                <path d="M11.96153846 4.14454242l-5.07692308 4.0843966-2.84615384.26472942C3.4423077 8.55039617 3 9.0420365 3 9.6093138v3.78184872c0 .58618655.4423077 1.07782688 1.03846154 1.11564538l2.84615384.2647294 5.07692308 4.0843966C12.38461538 19.1963003 13 18.8937524 13 18.3642936V4.6172735c-.01923077-.51054957-.63461538-.81309746-1.03846154-.47273108zM16.3828122 7.1452725c-.1331802-.16319294-.3995406-.19945804-.5707722-.05439765l-.6849266.58024153c-.152206.12692783-.1712317.3445184-.0380515.50771134.7610296.9066274 1.2176473 2.06711044 1.2176473 3.31825624 0 1.2511458-.4566177 2.41162886-1.2176473 3.31825624-.1331802.1450604-.1141545.3807835.0380515.5077113l.6849266.5802416c.1712316.1450604.4185662.1269278.5707722-.0543977C17.3911763 14.6521473 18 13.12901326 18 11.4789514c0-1.65006183-.5897979-3.13693075-1.6171878-4.3336789z" />
                <path d="M18.457814 5.13072624c-.1423624-.16751425-.4270872-.16751425-.5897871-.0372254l-.7321496.61421892c-.1626999.13028886-.1830374.3722539-.040675.52115545 1.3015993 1.43317747 2.0744238 3.2758342 2.0744238 5.2673925s-.7728245 3.834215-2.0744238 5.2673925c-.1423624.1675143-.1220249.3908666.040675.5211555l.7321496.6142189c.1626999.1489015.4474247.1302888.5897871-.0372254C20.0441381 16.1308286 21 13.91591798 21 11.477655c0-2.43826295-.9558619-4.6159482-2.542186-6.34692876z" />
              </g>
            </svg>
          </button>
        </div>
      </div>

      <!-- Queue labels -->
      <div class="queue-labels">
        <div class="label-queue">
          {{ $t('queue') }}
        </div>
        <div class="label-tickets-waiting">
          {{ $t('number.tickets') }}
        </div>
        <div class="label-next-ticket">
          {{ $t('next') }}
        </div>
        <div class="label-next-ticket-waiting">
          {{ $t('waiting.time') }}
        </div>
      </div>

      <div
        v-for="queue in sortedServiceQueues"
        :key="queue.letter"
        :disabled="!service.active || !loggedUser.desk"
        class="queue-row queue-active queue-row-element flex"
      >
        <div class="queue">
          <div
            id="queue-letter"
            class="queue-letter"
          >
            {{ queue.letter }}
          </div>
          <div
            id="queue-name"
            class="queue-name"
          >
            {{ queue.name[language] }}
          </div>
        </div>
        <div
          id="queue-waiting-count"
          class="tickets-waiting"
        >
          {{ ticketsWaitingCount[queue.letter] | formatWaitCount }}
        </div>

        <div class="next-ticket">
          <div
            id="queue-next-letter"
            class="next-ticket-letter"
          >
            {{ queue | ifNextTicketNull('letter', '--') }}
          </div>
          <div
            id="queue-next-number"
            class="next-ticket-number"
          >
            {{ queue | ifNextTicketNull('number', '') }}
          </div>
        </div>

        <wait-time
          :next-ticket="queue.nextTicket"
        />
        <div
          class="queue-action action-disabled"
        >
          <button
            :id="`call-btn-${queue.letter}`"
            :disabled="callBtnDisable || !queue.nextTicket"
            class="btn-call"
            :title="$t('call.ticket')"
            @click="callNextTicket({queue: queue.letter})"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g
                class="icon-call"
                fill-rule="nonzero"
                fill="#0076DC"
              >
                <path d="M11.96153846 4.14454242l-5.07692308 4.0843966-2.84615384.26472942C3.4423077 8.55039617 3 9.0420365 3 9.6093138v3.78184872c0 .58618655.4423077 1.07782688 1.03846154 1.11564538l2.84615384.2647294 5.07692308 4.0843966C12.38461538 19.1963003 13 18.8937524 13 18.3642936V4.6172735c-.01923077-.51054957-.63461538-.81309746-1.03846154-.47273108zM16.3828122 7.1452725c-.1331802-.16319294-.3995406-.19945804-.5707722-.05439765l-.6849266.58024153c-.152206.12692783-.1712317.3445184-.0380515.50771134.7610296.9066274 1.2176473 2.06711044 1.2176473 3.31825624 0 1.2511458-.4566177 2.41162886-1.2176473 3.31825624-.1331802.1450604-.1141545.3807835.0380515.5077113l.6849266.5802416c.1712316.1450604.4185662.1269278.5707722-.0543977C17.3911763 14.6521473 18 13.12901326 18 11.4789514c0-1.65006183-.5897979-3.13693075-1.6171878-4.3336789z" />
                <path d="M18.457814 5.13072624c-.1423624-.16751425-.4270872-.16751425-.5897871-.0372254l-.7321496.61421892c-.1626999.13028886-.1830374.3722539-.040675.52115545 1.3015993 1.43317747 2.0744238 3.2758342 2.0744238 5.2673925s-.7728245 3.834215-2.0744238 5.2673925c-.1423624.1675143-.1220249.3908666.040675.5211555l.7321496.6142189c.1626999.1489015.4474247.1302888.5897871-.0372254C20.0441381 16.1308286 21 13.91591798 21 11.477655c0-2.43826295-.9558619-4.6159482-2.542186-6.34692876z" />
              </g>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div
      :disabled="!service.active"
      class="main-sidebar"
    >
      <!-- add class .show-panel to show ticket-user-panel -->
      <div
        v-if="currentTicket !== null"
        :class="{'show-panel': currentTicket.state !== undefined}"
        class="main-sidebar-panel ticket-user-panel"
      >
        <div class="current-ticket">
          <header>
            <h2
              v-if="currentTicket.state === 'attending'"
              class="sidebar-title"
            >
              {{ $t('attending') }}
            </h2>
            <h2
              v-else
              class="sidebar-title"
            >
              {{ $t('calling') }}<span class="dots--calling" />
            </h2>
            <time-counter
              v-if="currentTicket.state === 'attending'"
              :since="latestSession"
            />
          </header>
          <div class="ticket-details">
            <div
              id="called-ticket"
              class="ticket-number"
            >
              {{ currentTicket.letter }} {{ currentTicket.number }}
            </div>
          </div>
          <div class="ticket-actions">
            <button
              v-if="currentTicket.state === 'called'"
              id="ticket-attend"
              type="button"
              class="btn btn--white"
              @click="openAttendUserModal()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
              >
                <g
                  fill="#0076DC"
                  fill-rule="evenodd"
                  stroke="#0076DC"
                  stroke-width="1.26"
                  transform="translate(3 3)"
                >
                  <path d="M.73898641 17.6514008a2.99780944 2.99780944 0 0 0-.00195917.0099855c.00085404-.0033354.00160861-.0066831.00226655-.0100404zM.80580745 17.37H17.1940976c-.0482203-.181183-.1237639-.4227378-.2319956-.709306-.2755533-.7295905-.6622503-1.4598207-1.1768138-2.1392343C14.3196642 12.5862957 12.1268528 11.43 9.00000001 11.43c-3.126453 0-5.32044321 1.1602664-6.78828867 3.1024154-.51545119.6820085-.90305117 1.415053-1.17946633 2.1474727-.10238238.2712835-.17565234.5024093-.22643756.6901119zm16.43993285.2220146c.0004219.0022236.0009564.004387.0016071.0064849a1.85737776 1.85737776 0 0 0-.001255-.006614z" />
                  <circle
                    cx="9"
                    cy="4.5"
                    r="3.87"
                  />
                </g>
              </svg>
              <span>{{ $t('receive.user') }}</span>
            </button>
            <hr v-if="currentTicket.state === 'called'">
            <button
              v-if="currentTicket.state === 'called'"
              id="ticket-recall"
              type="button"
              class="btn btn-light"
              @click="recallTicket()"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g
                  class="icon-call"
                  fill-rule="nonzero"
                  fill="#FFF"
                >
                  <path d="M11.96153846 4.14454242l-5.07692308 4.0843966-2.84615384.26472942C3.4423077 8.55039617 3 9.0420365 3 9.6093138v3.78184872c0 .58618655.4423077 1.07782688 1.03846154 1.11564538l2.84615384.2647294 5.07692308 4.0843966C12.38461538 19.1963003 13 18.8937524 13 18.3642936V4.6172735c-.01923077-.51054957-.63461538-.81309746-1.03846154-.47273108zM16.3828122 7.1452725c-.1331802-.16319294-.3995406-.19945804-.5707722-.05439765l-.6849266.58024153c-.152206.12692783-.1712317.3445184-.0380515.50771134.7610296.9066274 1.2176473 2.06711044 1.2176473 3.31825624 0 1.2511458-.4566177 2.41162886-1.2176473 3.31825624-.1331802.1450604-.1141545.3807835.0380515.5077113l.6849266.5802416c.1712316.1450604.4185662.1269278.5707722-.0543977C17.3911763 14.6521473 18 13.12901326 18 11.4789514c0-1.65006183-.5897979-3.13693075-1.6171878-4.3336789z" />
                  <path d="M18.457814 5.13072624c-.1423624-.16751425-.4270872-.16751425-.5897871-.0372254l-.7321496.61421892c-.1626999.13028886-.1830374.3722539-.040675.52115545 1.3015993 1.43317747 2.0744238 3.2758342 2.0744238 5.2673925s-.7728245 3.834215-2.0744238 5.2673925c-.1423624.1675143-.1220249.3908666.040675.5211555l.7321496.6142189c.1626999.1489015.4474247.1302888.5897871-.0372254C20.0441381 16.1308286 21 13.91591798 21 11.477655c0-2.43826295-.9558619-4.6159482-2.542186-6.34692876z" />
                </g>
              </svg>
              <span>{{ $t('call.again') }}</span>
            </button>
            <button
              v-if="currentTicket.state === 'called'"
              id="ticket-skip"
              type="button"
              class="btn btn-light"
              @click="skipTicket()"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g
                  class="icon-close"
                  transform="translate(5 5)"
                  fill="#FFF"
                  fill-rule="evenodd"
                >
                  <rect
                    transform="rotate(45 6.803 6.803)"
                    x="-1.19669914"
                    y="5.30330086"
                    width="16"
                    height="3"
                    rx="1.5"
                  />
                  <rect
                    transform="rotate(-45 6.8 6.8)"
                    x="-1.19893217"
                    y="5.30106783"
                    width="16"
                    height="3"
                    rx="1.5"
                  />
                </g>
              </svg>
              <span> {{ $t('user.missing') }} </span>
            </button>
            <button
              v-if="currentTicket.state === 'attending'"
              id="ticket-close"
              type="button"
              class="btn btn-light"
              @click="closeTicket()"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g
                  class="icon-call"
                  fill-rule="nonzero"
                  fill="#FFF"
                >
                  <path d="M11.96153846 4.14454242l-5.07692308 4.0843966-2.84615384.26472942C3.4423077 8.55039617 3 9.0420365 3 9.6093138v3.78184872c0 .58618655.4423077 1.07782688 1.03846154 1.11564538l2.84615384.2647294 5.07692308 4.0843966C12.38461538 19.1963003 13 18.8937524 13 18.3642936V4.6172735c-.01923077-.51054957-.63461538-.81309746-1.03846154-.47273108zM16.3828122 7.1452725c-.1331802-.16319294-.3995406-.19945804-.5707722-.05439765l-.6849266.58024153c-.152206.12692783-.1712317.3445184-.0380515.50771134.7610296.9066274 1.2176473 2.06711044 1.2176473 3.31825624 0 1.2511458-.4566177 2.41162886-1.2176473 3.31825624-.1331802.1450604-.1141545.3807835.0380515.5077113l.6849266.5802416c.1712316.1450604.4185662.1269278.5707722-.0543977C17.3911763 14.6521473 18 13.12901326 18 11.4789514c0-1.65006183-.5897979-3.13693075-1.6171878-4.3336789z" />
                  <path d="M18.457814 5.13072624c-.1423624-.16751425-.4270872-.16751425-.5897871-.0372254l-.7321496.61421892c-.1626999.13028886-.1830374.3722539-.040675.52115545 1.3015993 1.43317747 2.0744238 3.2758342 2.0744238 5.2673925s-.7728245 3.834215-2.0744238 5.2673925c-.1423624.1675143-.1220249.3908666.040675.5211555l.7321496.6142189c.1626999.1489015.4474247.1302888.5897871-.0372254C20.0441381 16.1308286 21 13.91591798 21 11.477655c0-2.43826295-.9558619-4.6159482-2.542186-6.34692876z" />
                </g>
              </svg>
              <span> {{ $t('close.ticket') }} </span>
            </button>
            <button
              v-if="currentTicket.state === 'attending'"
              id="ticket-pause"
              type="button"
              class="btn btn-light"
              @click="pauseTicket()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
              >
                <g
                  fill="#FFF"
                  class="icon-call"
                  fill-rule="evenodd"
                  transform="translate(7 5)"
                >
                  <rect
                    width="14"
                    height="3"
                    x="1.5"
                    y="5.5"
                    rx="1.5"
                    transform="rotate(90 8.5 7)"
                  />
                  <rect
                    width="14"
                    height="3"
                    x="-5.5"
                    y="5.5"
                    rx="1.5"
                    transform="rotate(-90 1.5 7)"
                  />
                </g>
              </svg>
              <span>{{ $t('pause.ticket') }}</span>
            </button>
          </div>
        </div>
        <div
          v-if="currentUser && currentTicket.assignedTo !== null"
          id="assigned-user-data"
          class="user-client"
        >
          <div class="ticket-user flex">
            <div class="avatar">
              <img
                id="assigned-user-avatar"
                :src="currentUser.avatar"
              >
            </div>
            <div class="">
              <div
                id="assigned-user-name"
                class="ticket-user-name"
              >
                {{ currentUser.name }}
              </div>
            </div>
          </div>
          <div class="ticket-user-details">
            <dl class="details-list">
              <dt>Técnico ID</dt>
              <dd
                id="assigned-user-username"
                class="user-ist-id"
              >
                {{ currentUser.username }}
              </dd>
              <button
                type="button"
                class="btn btn-primary btn-arrow-right"
                :aria-label="$t('student.page')"
                @click="handleTicketActionBtn()"
              >
                {{ $t('student.page') }}
              </button>
            </dl>
          </div>
        </div>
        <div
          v-if="currentTicket.assignedTo === null && currentTicket.state !== 'called'"
          id="assigned-user-data"
          class="user-client"
        >
          <div class="ticket-user flex">
            <div class="avatar">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
              >
                <g
                  fill="none"
                  fill-rule="evenodd"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="16"
                    fill="#2E3242"
                    opacity=".37999999"
                  />
                  <path
                    fill="#FFF"
                    d="M14.772 18.842c-.0600003-.408002-.0450004-.7799983.045-1.116.0900004-.3360017.2189992-.6479986.387-.936.1680008-.2880014.356999-.5519988.567-.792.210001-.2400012.410999-.4709989.603-.693.192001-.2220011.3509994-.437999.477-.648.1260006-.2100011.189-.4289989.189-.657 0-.3720019-.1109989-.653999-.333-.846-.2220011-.192001-.5129982-.288-.873-.288-.3360017 0-.6329987.0779992-.891.234s-.5069988.3539988-.747.594l-1.458-1.332c.408002-.4920025.8879972-.8879985 1.44-1.188.5520028-.3000015 1.1639966-.45 1.836-.45.5040025 0 .9749978.0599994 1.413.18.4380022.1200006.8159984.3059987 1.134.558.3180016.2520013.5669991.572998.747.963.1800009.3900019.27.8489974.27 1.377 0 .3720019-.0659993.7049985-.198.999-.1320007.2940015-.296999.5669987-.495.819-.198001.2520013-.4079989.4949988-.63.729-.2220011.2340012-.4259991.4769987-.612.729-.1860009.2520013-.3329995.5189986-.441.801-.1080005.2820014-.1440002.6029982-.108.963h-2.322zm-.432 2.718c0-.4800024.1499985-.8789984.45-1.197.3000015-.3180016.6779977-.477 1.134-.477.4560023 0 .8369985.1589984 1.143.477.3060015.3180016.459.7169976.459 1.197 0 .4680023-.1529985.8609984-.459 1.179-.3060015.3180016-.6869977.477-1.143.477-.4560023 0-.8339985-.1589984-1.134-.477-.3000015-.3180016-.45-.7109977-.45-1.179z"
                  />
                </g>
              </svg>
            </div>
            <div>
              <div
                id="assigned-user-name"
                class="ticket-user-name"
              >
                Utente Desconhecido
              </div>
              <div>
                <button
                  class="link link--blue"
                  @click="openAssignUserToTicketModal()"
                >
                  Associar Técnico ID
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- operator's panel -->
      <div class="main-sidebar-panel operator-panel">
        <div class="operator flex">
          <div class="avatar">
            <img
              :src="loggedUser.avatar"
              alt=""
            >
          </div>
          <div class="welcome">
            <p v-if="!loggedUser.desk">
              {{ $t('welcome') }}
            </p>
            <div class="operator-name">
              {{ loggedUser.name | formatName }}
            </div>
            <button
              v-if="loggedUser.desk"
              id="operator-panel-desk"
              type="button"
              class="operator-table link-modal"
              :title="$t('choose.desk')"
              aria-haspopup="true"
              aria-expanded="false"
              @click="openChooseDeskModal()"
            >
              {{ $t('desk.with.number', { desk: loggedUser.desk }) }}
            </button>
            <button
              v-if="loggedUser.desk > 0"
              id=""
              :title="$t('leave.desk')"
              type="button"
              class="leave-table link-modal"
              aria-haspopup="true"
              aria-expanded="false"
              @click="leaveDesk()"
            >
              <span class="">{{ $t('leave.desk') }}</span>
              <span class="leave-table__icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                >
                  <path
                    class="icon-fill"
                    fill="#8F95A1"
                    fill-rule="evenodd"
                    d="M5 12l4.6666667-3.88888889v2.33333329h5.44444441v3.1111112H9.6666667v2.3333333L5 12zm12.44444444-5.44444444H11.2222222V5h6.22222224C18.3 5 19 5.7 19 6.55555556V17.4444444C19 18.3 18.3 19 17.44444444 19H11.2222222v-1.5555556h6.22222224V6.55555556z"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
        <!-- to choose a specific ticket -->
        <div
          v-if="loggedUser.desk"
          class="message"
        >
          <button
            type="button"
            class="btn btn-secondary btn-outline btn--full-width btn-icon--left link-modal"
            aria-haspopup="true"
            aria-expanded="false"
            @click="openTakeTicketModal()"
          >
            <span class="btn__icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="20"
              >
                <g
                  fill="none"
                  fill-rule="evenodd"
                >
                  <path
                    fill="#45555F"
                    d="M7.99748681 0h2.77199759c.4602704 0 .8333933.37312287.8333933.83339326V18.9053088c0 .4602704-.3731229.8333933-.8333933.8333933H7.99748681c-.1243308-1.09429-1.06010801-1.9447419-2.19604796-1.9447419-1.13593996 0-2.07171716.8504519-2.19604796 1.9447419H.83339326C.37312286 19.7387021 0 19.3655792 0 18.9053088V.83339326C0 .37312287.37312287 0 .83339326 0h2.77199763c.1243308 1.09428993 1.060108 1.94474191 2.19604796 1.94474191 1.13593995 0 2.07171716-.85045198 2.19604796-1.94474191z"
                  />
                  <path
                    fill="#FFF"
                    d="M2.7948344 9.37339497h5.26747377c.24962023 0 .4519774.20235717.4519774.4519774 0 .24962023-.20235717.45197743-.4519774.45197743H2.7948344c-.24962022 0-.4519774-.2023572-.4519774-.45197743 0-.24962023.20235718-.4519774.4519774-.4519774z"
                  />
                  <rect
                    width="5.02857143"
                    height="1"
                    x="2.342857"
                    y="12.077401"
                    fill="#FFF"
                    rx=".5"
                  />
                  <path
                    fill="#FFF"
                    d="M3.03122773 6.375706h5.13754454c.34861745 0 .63122773.28261028.63122773.63122773 0 .34861744-.28261028.63122772-.63122773.63122772H3.03122773C2.68261028 7.63816145 2.4 7.35555117 2.4 7.00693373c0-.34861745.28261028-.63122773.63122773-.63122773z"
                  />
                </g>
              </svg>
            </span>
            <span class="btn__text">
              {{ $t('pick.a.ticket') }}
            </span>
          </button>
        </div>
        <!-- in case user didn't choose a table yet show this message -->
        <div
          v-if="!loggedUser.desk"
          class="message message-choose-table"
        >
          <p>{{ $t('welcome.message') }}</p>
          <button
            type="button"
            class="btn btn-primary btn-active link-modal"
            aria-haspopup="true"
            aria-expanded="false"
            :aria-label="$t('choose.desk')"
            @click="openChooseDeskModal()"
          >
            {{ $t('choose.desk') }}
          </button>
        </div>
        <!-- in case there's no tickets to call insert this in the DOM and remove style="display:none" -->
        <div
          v-if="!nextTicket"
          id="no-pending-tickets-msg"
          class="message message-no-tickets"
        >
          <p>{{ $t('no.tickets.to.call') }}</p>
        </div>
      </div>
    </div>
    <modal v-model="takeTicketModal">
      <take-ticket
        slot="modal-panel"
        v-model="takeTicketModal"
        :service-i-d="serviceID"
      />
    </modal>
    <modal v-model="chooseDeskModal">
      <choose-desk
        slot="modal-panel"
        v-model="chooseDeskModal"
      />
    </modal>
    <modal v-model="attendUserModal">
      <attend-user
        slot="modal-panel"
        v-model="attendUserModal"
        :service-i-d="serviceID"
      />
    </modal>
    <modal v-model="assignUserToTicketModal">
      <assign-user-to-ticket
        slot="modal-panel"
        v-model="assignUserToTicketModal"
        :service-i-d="serviceID"
        :ticket="currentTicket"
      />
    </modal>
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import tickets from '@/api/tickets'
import DeskAPI from '@/api/desk'
import ChooseDesk from '@/components/modals/ChooseDesk'
import AttendUser from '@/components/modals/AttendUser'
import TakeTicket from '@/components/modals/TakeTicket'
import TimeCounter from '@/components/TimeCounter'
import AssignUserToTicket from '@/components/modals/AssignUserToTicket'
import WaitTime from '@/components/WaitTime'
import Modal from '@/components/utils/Modal'
export default {
  components: {
    TimeCounter,
    WaitTime,
    Modal,
    TakeTicket,
    ChooseDesk,
    AttendUser,
    AssignUserToTicket
  },
  filters: {
    ifTicketNull (ticket, field, placeholder) {
      return ticket ? ticket[field] : placeholder
    },
    ifNextTicketNull (queue, field, placeholder) {
      return queue.nextTicket ? queue.nextTicket[field] : placeholder
    },
    formatWaitCount (count) {
      if (count === 0) {
        return '--'
      }
      return count
    },
    formatName (name) {
      let names = name.split(' ')
      name = name.trim()
      if (names.length < 3) {
        return name
      }
      return `${names[0]} ${names[names.length - 1]}`
    }
  },
  props: {
    serviceID: {
      type: String,
      required: true
    },
    socket: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      takeTicketModal: false,
      chooseDeskModal: false,
      attendUserModal: false,
      assignUserToTicketModal: false,
      clock: null
    }
  },
  computed: {
    ...mapState([
      'service',
      'loggedUser',
      'nextTickets',
      'ticketsWaitingCount',
      'currentTicket',
      'currentUser'
    ]),
    ...mapGetters(['sortedServiceQueues']),
    language () {
      return this.$i18n.locale()
    },
    callBtnDisable () {
      if (
        !this.service.active ||
        !this.loggedUser.desk ||
        this.currentTicket !== ''
      ) {
        return true
      }
      return false
    },
    // Calculates the waitTime of the mext ticket in the service
    nextTicketWaitTime () {
      try {
        if (this.nextTicket.waitTime === '--') {
          return null
        }
        return moment()
          .utc()
          .subtract(moment(this.nextTicket.createdAt).format('x'), 'ms')
          .format('H:m')
      } catch (error) {
        return null
      }
    },
    // Uses next tickets to compute the next ticket with a FIFO criteria
    nextTicket () {
      let nextTicket = null
      for (let letter of Object.keys(this.nextTickets)) {
        if (nextTicket !== null) {
          if (this.nextTickets[letter] !== null) {
            if (
              moment(this.nextTickets[letter].createdAt).format('x') <
              moment(nextTicket.createdAt).format('x')
            ) {
              nextTicket = this.nextTickets[letter]
            }
          }
        } else {
          nextTicket = this.nextTickets[letter]
        }
      }
      return nextTicket
    },
    latestSession () {
      return this.currentTicket && this.currentTicket.sessions
        ? moment(
          this.currentTicket.sessions[this.currentTicket.sessions.length - 1]
            .start
        )
        : null
    }
  },
  created () {
    this.fetchProfile()
    this.fetchCurrentTickets(this.serviceID)
    this.fetchService(this.serviceID)
    this.socket.on('connect', () => {
      this.socket.emit('register-operator', {
        type: 'operator',
        service: this.serviceID
      })
    })
  },
  async mounted () {
    if (!Notification) {
      console.log(
        'Desktop notifications not available in your browser. Try Chromium or Chrome.'
      )
    }

    if (Notification.permission !== 'granted') {
      Notification.requestPermission()
    }
    await this.fetchCurrentTicket(this.serviceID)
    await this.fetchNextTickets(this.serviceID)
    await this.fetchTicketsWaitingCount(this.serviceID)
    if (this.currentTicket) {
      await this.fetchFenixUser({ username: this.currentTicket.assignedTo })
    }

    // Socket IO
    this.socket.on('ticket-called', async val => {
      // this.updateQueueNumber({letter: val.letter, newTicket: val.newTicket, nextTicket: val.nextTicket})
      await this.fetchNextTickets(this.serviceID)
      await this.fetchTicketsWaitingCount(this.serviceID)
      await this.fetchCurrentTickets(this.serviceID)
    })
    this.socket.on('ticket-created', async ({ ticket }) => {
      let notification = new Notification('Senha emitida', {
        icon: '~@/assets/images/tickets-96.png',
        body: `A senha ${String(ticket.letter).toUpperCase()} ${
          ticket.number
        } foi emitida`
      })

      notification.onclick = function () {
        // window.open('http://stackoverflow.com/a/13328397/1269037')
      }
      await this.fetchTicketsWaitingCount(this.serviceID)
      await this.fetchNextTickets(this.serviceID)
    })
  },
  beforeDestroy () {
    this.socket.disconnect()
    clearInterval(this.clock)
  },
  methods: {
    ...mapActions([
      'fetchService',
      'fetchNextTickets',
      'fetchTicketsWaitingCount',
      'fetchCurrentTicket',
      'fetchCurrentTickets',
      'fetchCurrentTicket',
      'clearFenixUser',
      'fetchFenixUser',
      'updateQueueNumber',
      'fetchProfile'
    ]),
    async callNextTicket ({ queue }) {
      await tickets.callNextTicket({
        service: this.serviceID,
        queueSlug: queue
      })
      await this.fetchCurrentTicket(this.serviceID)
      if (this.currentTicket.assignedTo) {
        this.fetchFenixUser({ username: this.currentTicket.assignedTo })
      }
      this.fetchNextTickets(this.serviceID)
      this.fetchTicketsWaitingCount(this.serviceID)
    },
    async recallTicket () {
      await tickets.recallTicket({
        service: this.serviceID,
        queueSlug: this.currentTicket.letter,
        ticket: this.currentTicket.id
      })
    },
    async skipTicket () {
      await tickets.skipTicket({
        service: this.serviceID,
        queueSlug: this.currentTicket.letter,
        ticket: this.currentTicket.id
      })
      this.fetchCurrentTicket(this.serviceID)
    },
    async closeTicket () {
      await tickets.closeTicket({
        service: this.serviceID,
        queueSlug: this.currentTicket.letter,
        ticket: this.currentTicket.id
      })
      this.fetchCurrentTicket(this.serviceID)
      this.clearFenixUser()
    },
    async pauseTicket () {
      await tickets.pauseTicket({
        service: this.serviceID,
        queueSlug: this.currentTicket.letter,
        ticket: this.currentTicket.id
      })
      this.fetchCurrentTicket(this.serviceID)
    },
    async leaveDesk () {
      await DeskAPI.leave()
      await this.fetchProfile()
    },
    async handleTicketActionBtn () {
      window.open(
        `https://fenix.tecnico.ulisboa.pt/login?callback=https://fenix.tecnico.ulisboa.pt/academic-admin-office/students/search-for-students?username=${
          this.currentTicket.assignedTo
        }`
      )
    },
    openChooseDeskModal () {
      this.chooseDeskModal = true
    },
    openAssignUserToTicketModal () {
      this.assignUserToTicketModal = true
      // this.$showModal('AssignUserToTicket', AssignUserToTicket, { serviceID: this.serviceID, ticket: this.currentTicket })
    },
    openTakeTicketModal () {
      this.takeTicketModal = true
    },
    async openAttendUserModal () {
      // If ticket was emitted online there no need fot the modal
      if (this.currentTicket.assignedTo !== null) {
        await tickets.attendTicket({
          service: this.serviceID,
          queueSlug: this.currentTicket.letter,
          ticket: this.currentTicket.id
        })
        await this.fetchCurrentTicket(this.serviceID)
      } else {
        this.attendUserModal = true
      }
    }
  }
}
</script>
<style lang="scss">
.queue {
  margin-bottom: 0;
  display: flex;
  align-items: center;
}
.btn-call,
.btn-call--filled {
  border: 0.125rem solid #0076dc;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  position: relative;
  transition: all 0.3s ease;
}

.btn-call svg,
.btn-call--filled svg {
  position: absolute;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.btn-call .icon-call,
.btn-call--filled .icon-call {
  fill: #0076dc;
  transition: inherit;
}

.btn-call:hover,
.btn-call--filled:hover,
.btn-call:focus,
.btn-call--filled:focus,
.btn-call.btn-call--filled,
.btn-call--filled {
  background-color: #0076dc;
  color: #fff;
}

.btn-call:hover .icon-call,
.btn-call--filled:hover .icon-call,
.btn-call:focus .icon-call,
.btn-call--filled:focus .icon-call,
.btn-call.btn-call--filled .icon-call,
.btn-call--filled .icon-call {
  fill: #fff;
}

.btn-call.btn-disabled,
.btn-disabled.btn-call--filled,
.btn-call[disabled],
[disabled].btn-call--filled {
  background-color: transparent;
  border-color: #d8dee3;
  pointer-events: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.btn-call.btn-disabled .icon-call,
.btn-disabled.btn-call--filled .icon-call,
.btn-call[disabled] .icon-call,
[disabled].btn-call--filled .icon-call {
  fill: #d8dee3;
}

.btn-call--filled:hover,
.btn-call--filled:focus {
  background-color: #14a4ff;
  border-color: #14a4ff;
}

.btn-call--filled.btn-disabled,
.btn-call--filled[disabled] {
  background-color: #d8dee3;
  border-color: #d8dee3;
  pointer-events: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.btn-call--filled.btn-disabled .icon-call,
.btn-call--filled[disabled] .icon-call {
  fill: #fff;
}
.leave-table__icon {
  width: 1.5rem;
  height: 1.5rem;
}

.leave-table__icon .icon-fill {
  transition: fill 0.2s ease-in-out;
}

.ticket-user-panel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: #fff;
  box-shadow: 0 0.1875rem 0.4375rem 0 rgba(0, 0, 0, 0.1);
  z-index: 1;
  will-change: transform;
  transition: all 0.3s ease;
  transform: translateX(100%);
}

.ticket-user-panel.show-panel {
  -webkit-transform: translateX(0);
  transform: translateX(0);
}

.ticket-user-panel.show-panel + .operator-panel {
  pointer-events: none;
}

.ticket-user-panel .btn,
.ticket-user-panel .btn-primary,
.ticket-user-panel .btn-secondary,
.ticket-user-panel .btn-light,
.ticket-user-panel .btn-warning {
  width: 100%;
  text-align: left;
  font-size: 0.875rem;
  text-transform: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.ticket-user-panel .btn ~ .btn,
.ticket-user-panel .btn-primary ~ .btn,
.ticket-user-panel .btn-secondary ~ .btn,
.ticket-user-panel .btn-light ~ .btn,
.ticket-user-panel .btn-warning ~ .btn,
.ticket-user-panel .btn ~ .btn-primary,
.ticket-user-panel .btn-primary ~ .btn-primary,
.ticket-user-panel .btn-secondary ~ .btn-primary,
.ticket-user-panel .btn-light ~ .btn-primary,
.ticket-user-panel .btn-warning ~ .btn-primary,
.ticket-user-panel .btn ~ .btn-secondary,
.ticket-user-panel .btn-primary ~ .btn-secondary,
.ticket-user-panel .btn-secondary ~ .btn-secondary,
.ticket-user-panel .btn-light ~ .btn-secondary,
.ticket-user-panel .btn-warning ~ .btn-secondary,
.ticket-user-panel .btn ~ .btn-light,
.ticket-user-panel .btn-primary ~ .btn-light,
.ticket-user-panel .btn-secondary ~ .btn-light,
.ticket-user-panel .btn-light ~ .btn-light,
.ticket-user-panel .btn-warning ~ .btn-light,
.ticket-user-panel .btn ~ .btn-warning,
.ticket-user-panel .btn-primary ~ .btn-warning,
.ticket-user-panel .btn-secondary ~ .btn-warning,
.ticket-user-panel .btn-light ~ .btn-warning,
.ticket-user-panel .btn-warning ~ .btn-warning {
  margin-top: 0.625rem;
}

.ticket-user-panel .btn svg,
.ticket-user-panel .btn-primary svg,
.ticket-user-panel .btn-secondary svg,
.ticket-user-panel .btn-light svg,
.ticket-user-panel .btn-warning svg {
  margin-right: 0.625rem;
}

.current-ticket {
  background-color: #0076dc;
  color: #fff;
  padding: 1.875rem;
}

.current-ticket .sidebar-title {
  color: inherit;
}

.current-ticket header {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  margin-bottom: 1.875rem;
}

.current-ticket .running-time {
  font-size: 1.25rem;
  font-weight: 600;
}

.current-ticket .ticket-number {
  font-weight: 700;
  font-size: 2.125rem;
  text-transform: uppercase;
}

.current-ticket .queue-name {
  font-weight: 600;
  font-size: 1.125rem;
  margin-top: 0.3125rem;
}

.current-ticket .ticket-actions {
  margin-top: 1.875rem;
}
</style>
